import React, { useMemo, useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { InfoCircleOutlined } from "@ant-design/icons"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import TripDetailsCard from "./TripDetailsCard"
import PaymentMethod from "./PaymentMethod"
import PreviousPriceDetail from "./PreviousPriceDetail"
import styles from "./BookingForm.module.css"
import Stepper from "./Stepper"
import { useSelector } from "react-redux"
import PriceBreakdown from "./PriceBreakdown"
import RefundPayment from "./RefundPayment"
import { PAYMENT_OPTIONS } from "../constants"
import { numberToCurrency } from "helpers/string"
import { calculatePaymentBreakdown, getCardFeeFraction, isBalanceInCash } from "utils/bookingCalculations"
import { paymentMethodsSelector, selectedPaymentMethodIdSelector, paymentMethodsLoadingSelector } from "store/user"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { hasNoUserBookingChanges, resolveEditPaymentType } from "../bookingUtils"
import AddOnsSelector from "components/bookings/AddOnsSelector"
import { calculateAddOnsTotal } from "components/bookings/helpers"
import PromoCodeInput from "ui/PromoCodeInput"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"

const { Title } = Typography

// Payment related calculations
const usePaymentCalculations = ({ booking, bookingSelections, depositPercent, customDepositAmount, t }) => {
  const { data } = useSelector((state) => state.bookings.refundDetails)
  const { non_refundable_amount = 0, cash_incomes, card_incomes, stripe_incomes } = data || {}
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash, full_cash } = PAYMENT_OPTIONS
  const hasCashPayment = booking?.payment_option === full_cash
  const hasDepositPayment = [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option)

  // bookingSelections.amount is already net of the discount (applied before tax in
  // calculateBookingTotal), so calculatePaymentBreakdown must not subtract it again.
  const { balanceAmount, refundDueNow } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount
  })

  // Check if the newly selected booking is less than 24 hours away
  // Use bookingSelections if available (edited date/time), otherwise fall back to original booking
  const isLessThan24h = useMemo(() => {
    const selectedDate = bookingSelections?.date
    const selectedTime = bookingSelections?.timeSlot

    if (selectedDate && selectedTime) {
      // Extract start time from range format (e.g., "9:00 AM - 11:00 AM" -> "9:00 AM")
      const startTime = selectedTime.split(" - ")[0].trim()

      // Create ISO format datetime string for proper parsing
      const bookingDateTime = new Date(`${selectedDate} ${startTime}`)
      const now = new Date()
      const hoursUntilBooking = (bookingDateTime - now) / (1000 * 60 * 60)
      return hoursUntilBooking < 24 && hoursUntilBooking > 0
    }

    // Fallback to original booking data if no new date/time selected
    return booking?.less_then_24h
  }, [bookingSelections?.date, bookingSelections?.timeSlot, booking?.less_then_24h])

  let refundAlert
  if (isLessThan24h && !hasCashPayment) {
    refundAlert = t("booking.non_refundable_24h", { amount: numberToCurrency(refundDueNow) })
  } else if (hasCashPayment) {
    refundAlert = t("booking.refund_alert_cash", {
      amount: numberToCurrency(refundDueNow)
    })
  } else {
    const cardNumber =
      Array.isArray(stripe_incomes) && stripe_incomes?.length > 0 && stripe_incomes[0]?.method && stripe_incomes[0]?.last4
        ? `(${stripe_incomes[0].method} **** ${stripe_incomes[0].last4})`
        : ""

    refundAlert = t("booking.refund_alert", {
      amount: numberToCurrency(refundDueNow),
      card_number: cardNumber
    })
  }

  return {
    hasCashPayment,
    hasDepositPayment,
    balanceAmount,
    refundDueNow,
    refundAlert,
    non_refundable_amount,
    cash_incomes,
    card_incomes,
    isLessThan24h
  }
}

// Price comparison: compare what the user actually sees (final totals) on both sides.
// Previous = booking.payment_summary.total_price (already net of discount)
// Updated  = bookingSelections.amount (already net of discount — applied before tax in
//            calculateBookingTotal; also includes addOnsTotal)
const usePriceComparison = (booking, bookingSelections) => {
  return useMemo(() => {
    const previousTotal = Number(booking?.payment_summary?.total_price || 0)
    const updatedTotal = Math.max(0, Number(bookingSelections?.amount || 0))
    const diff = updatedTotal - previousTotal
    return {
      isChanged: Math.abs(diff) >= 0.01,
      isDecreased: diff <= -0.01
    }
  }, [booking?.payment_summary?.total_price, bookingSelections?.amount])
}

const EditBookingForm = ({
  form,
  bookingUuid,
  bookAgain,
  booking,
  onSubmit,
  onCancelModal,
  onValuesChange,
  appliedPromos = [],
  onPromoApplied,
  onPromoRemoved,
  isExperienceChanged = false,
  bookingSelections,
  updateBookingSelections
}) => {
  const { t } = useTranslation()
  const [alertMessage, setAlertMessage] = useState("")
  const [currentStep, setCurrentStep] = useState(1)

  const {
    experienceById: { experienceDetail, loading: experienceDetailLoading }
  } = useSelector((state) => state.experiences)
  const paymentMethodsLoading = useSelector(paymentMethodsLoadingSelector)
  const bookingLoading = useSelector((state) => state.bookings?.loading)

  // Ref keeps the latest bookingSelections available inside the step-2 useEffect
  // without adding bookingSelections to the deps array (which would cause a rerender loop).
  const bookingSelectionsRef = React.useRef(bookingSelections)
  useEffect(() => {
    bookingSelectionsRef.current = bookingSelections
  })

  const paymentMethods = useSelector(paymentMethodsSelector)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const selectedCard = paymentMethods.find((m) => m.payment_method_id === selectedPaymentMethodId)

  const editCardFeeEnabled = !!(
    experienceDetail?.assigned_custom_fees?.some((f) => f.id === null && f.title === "Card Processing Fee" && f.is_active !== false) ||
    Number(booking?.payment_summary?.card_processing_fee) > 0
  )
  // For payment links there's no selected card, so fall back to the experience's configured "Card
  // Processing Fee" rate (not a hardcoded 2.9%) so links use the same rate as card payments. The
  // 2.9% is kept only as a last resort for legacy bookings whose experience no longer carries the fee.
  const cardFeeInfo = useMemo(() => {
    if (selectedCard?.card_fee_info) return selectedCard.card_fee_info
    if (!editCardFeeEnabled) return null
    const configuredCardFee = experienceDetail?.assigned_custom_fees?.find(
      (f) => f.id === null && f.title === "Card Processing Fee" && f.is_active !== false
    )
    return { rate: configuredCardFee ? Number(configuredCardFee.price) / 100 : 0.029, fixed: 0.3 }
  }, [selectedCard?.card_fee_info, editCardFeeEnabled, experienceDetail?.assigned_custom_fees])

  const clientName = useWatch("existing_client", form)
  const clientFirstName = useWatch("firstName", form)
  const clientLastName = useWatch("lastName", form)
  const clientEmail = useWatch("email", form)
  const clientPhone = useWatch("phone_number", form)
  const depositPercent = useWatch("depositPercent", form) || booking?.deposit_percentage || ""
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentType = useWatch("paymentType", form)
  // Negotiated subtotal carried from booking creation. It's read-only in edit mode but must keep
  // overriding the computed guest subtotal on every recalc so the adjusted price holds through edits.
  // Fall back to the booking's stored adjusted subtotal so the override holds even before the watched
  // form field (seeded asynchronously from the booking) settles.
  const adjustedPrice = useWatch("adjustedPrice", form)
  const adjustedBase = adjustedPrice != null
    ? adjustedPrice
    : (booking?.payment_summary?.subtotal_adjusted ? Number(booking?.payment_summary?.adjusted_subtotal) : null)
  const overrideGuestSubtotal = adjustedBase != null && adjustedBase > 0 ? adjustedBase : undefined
  // The watched `paymentType` can be undefined when PaymentMethod is unmounted (e.g. when a
  // refund is shown). Recomputing with undefined drops the platform/card fees, so fall back to
  // the booking's saved payment type for edit-mode recalculation.
  const effectivePaymentType = resolveEditPaymentType(paymentType, booking, true)
  const isClientDetailsFilled =
    !!(((clientFirstName && clientLastName) || clientName) && (clientEmail || clientPhone?.fullNumber)) && !alertMessage
  // In edit mode the Next button must wait for every load to settle before allowing Step 2.
  // Otherwise a recalc on Step 2 can fire with partial data (missing card processing fee from
  // assigned_custom_fees, missing payment method info) and overwrite the booking's correct total.

  const {
    hasCashPayment,
    hasDepositPayment,
    balanceAmount,
    refundDueNow,
    refundAlert,
    non_refundable_amount,
    cash_incomes,
    card_incomes,
    isLessThan24h
  } = usePaymentCalculations({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount,
    t
  })
  // For deposit bookings the card fee applies only to the deposit (pay-now) portion.
  const depositFraction = getCardFeeFraction({
    depositOnly: hasDepositPayment,
    depositPercent,
    customDepositAmount,
    balanceInCash: isBalanceInCash(booking?.payment_option)
  })
  // bookingSelections.amount is already net of the discount, so the comparison needs no separate
  // discount adjustment.
  const { isChanged: isPriceChanged, isDecreased: isPriceDecreased } = usePriceComparison(booking, bookingSelections)

  // The updated-price section is shown only when the final total actually changes (increase or
  // decrease). A user edit that leaves the total unchanged — e.g. reverting add-ons back to their
  // original quantities, or a date/duration change on a negotiated-subtotal booking whose base is
  // pinned to the adjusted price — keeps the booking's saved total, so the "price not changed"
  // message is shown instead (matching a normal booking).
  // Exception: on an adjusted booking a guest-count edit doesn't move the total (the base is pinned),
  // but the breakdown must still reflect the new counts, so show the updated panel for guest/add-on
  // edits — hasNoUserBookingChanges ignores date/duration for these bookings, so it's true only when
  // guests and add-ons are unchanged.
  const hasAdjustedGuestOrAddonEdits = !!booking?.payment_summary?.subtotal_adjusted && !hasNoUserBookingChanges(bookingSelections, booking)
  const showUpdatedBreakdown = isPriceChanged || hasAdjustedGuestOrAddonEdits

  const handleAddOnsChange = useCallback(
    ({ target: { value: selections } }) => {
      const addOnsTotal = calculateAddOnsTotal(selections, experienceDetail?.add_ons)
      const counts = bookingSelections?.counts
      const tripLength = bookingSelections?.tripLength
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
      const update = { addOns: selections, addOnsTotal }
      if (hasGuestCounts && tripLength) {
        const projectedSelections = { ...bookingSelections, addOns: selections, addOnsTotal }
        if (hasNoUserBookingChanges(projectedSelections, booking)) {
          // User reverted all add-ons to original — restore the authoritative stored total to avoid
          // phantom "price changed" from rounding/fee-recalculation differences.
          update.amount = Number(booking?.payment_summary?.total_price) || 0
        } else {
          const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
          const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(
            projectedSelections,
            counts,
            guideFeePercent,
            {
              paymentType: effectivePaymentType,
              isEdit: true,
              originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
              customFees: experienceDetail?.assigned_custom_fees,
              platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
              addOnsCatalog: experienceDetail?.add_ons,
              cardFeeInfo,
              originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
              discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, !isExperienceChanged),
              dayAdjustments: experienceDetail?.day_adjustments,
              seasonalAdjustments: experienceDetail?.seasonal_adjustments,
              seasonRanges: experienceDetail?.available_date_ranges,
              depositFraction,
              overrideGuestSubtotal
            }
          ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
          update.amount = total
          update.baseAmount = guestSubtotal
          update.originalBaseAmount = calculatedGuestSubtotal
          update.breakdown = breakdown
          update.preCardTotal = preCardTotal
          update.cardProcessingFee = cardProcessingFee
        }
      }
      updateBookingSelections(update)
    },
    [updateBookingSelections, booking, bookingSelections, effectivePaymentType, cardFeeInfo, experienceDetail?.add_ons,
     experienceDetail?.guide_booking_fee_percentage, experienceDetail?.assigned_custom_fees, experienceDetail?.platform_fee_setting,
     experienceDetail?.day_adjustments,
     experienceDetail?.seasonal_adjustments,
     experienceDetail?.available_date_ranges, appliedPromos, isExperienceChanged, depositFraction, overrideGuestSubtotal]
  )

  // Navigation state
  const isStep1 = currentStep === 1
  const isStep2 = currentStep === 2

  // Derived state
  const priceDecreasedNoRefund = isStep2 && isPriceDecreased && refundDueNow === 0 && balanceAmount === 0
  const depositDecreasedNoRefund = isStep2 && isPriceDecreased && hasDepositPayment

  // Navigation handlers
  const goToStep2 = async () => {
    try {
      await form.validateFields()
      setCurrentStep(2)
    } catch (error) {
      console.error("Form validation failed:", error)
    }
  }

  const goToStep1 = () => setCurrentStep(1)

  // Amount and breakdown are now calculated and stored in Redux when guest counts or trip duration change
  // However, when payment type changes on Step 2, ExperienceDetail is unmounted, so we handle it here
  useEffect(() => {
    // Only recalculate if we have the necessary data and we're on step 2 (where payment method is selected)
    // Skip until experienceDetail is loaded — otherwise an incomplete calc (missing fee settings)
    // overwrites the booking's correct total and causes a phantom "refund due".
    // Also skip if the user hasn't actually changed anything — the booking's saved total is the
    // source of truth until then.
    if (hasNoUserBookingChanges(bookingSelectionsRef.current, booking)) return
    if (currentStep === 2 && effectivePaymentType && experienceDetail?.id) {
      const sel = bookingSelectionsRef.current
      const counts = sel?.counts
      const tripLength = sel?.tripLength
      const currentAmount = sel?.amount
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))

      if (hasGuestCounts && tripLength) {
        const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
        const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(sel, counts, guideFeePercent, {
          paymentType: effectivePaymentType,
          isEdit: true,
          originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
          customFees: experienceDetail?.assigned_custom_fees,
          platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
          addOnsCatalog: experienceDetail?.add_ons,
          cardFeeInfo,
          originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
          discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, !isExperienceChanged),
          dayAdjustments: experienceDetail?.day_adjustments,
          seasonalAdjustments: experienceDetail?.seasonal_adjustments,
          seasonRanges: experienceDetail?.available_date_ranges,
          depositFraction,
          overrideGuestSubtotal
        }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

        // Only update if amount is different (to avoid infinite loops)
        if (total !== currentAmount) {
          updateBookingSelections({
            amount: total,
            baseAmount: guestSubtotal,
            originalBaseAmount: calculatedGuestSubtotal,
            breakdown,
            preCardTotal,
            cardProcessingFee
          })
        }
      }
    }
  }, [
    effectivePaymentType,
    currentStep,
    experienceDetail?.id,
    bookingSelections?.counts,
    bookingSelections?.tripLength,
    bookingSelections?.amount,
    experienceDetail?.guide_booking_fee_percentage,
    experienceDetail?.assigned_custom_fees,
    experienceDetail?.add_ons,
    experienceDetail?.platform_fee_setting,
    experienceDetail?.day_adjustments,
    experienceDetail?.seasonal_adjustments,
    experienceDetail?.available_date_ranges,
    booking,
    booking?.booking_fee_percentage,
    booking?.payment_summary?.tax_and_fee,
    booking?.platform_fee_setting,
    cardFeeInfo,
    updateBookingSelections,
    appliedPromos,
    isExperienceChanged,
    depositFraction,
    overrideGuestSubtotal
  ])

  // Tax & fees are computed on the discounted base, so changing the promo must recompute the
  // total. hasNoUserBookingChanges (above) ignores promos and would skip this, so handle promo
  // changes separately. A ref captures the initial promo set (which includes the booking's
  // original discount) once the booking has loaded, so the authoritative saved total is preserved
  // on first render — we only recompute once the user actually adds/removes a promo.
  const initialPromoKeyRef = React.useRef(null)
  useEffect(() => {
    const sel = bookingSelectionsRef.current
    const counts = sel?.counts
    const tripLength = sel?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!experienceDetail?.id || !hasGuestCounts || !tripLength) return

    const promoKey = (appliedPromos || [])
      .map((p) => p.code)
      .sort()
      .join("|")
    if (initialPromoKeyRef.current === null) {
      initialPromoKeyRef.current = promoKey
      return
    }
    if (promoKey === initialPromoKeyRef.current) return
    initialPromoKeyRef.current = promoKey

    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(sel, counts, guideFeePercent, {
      paymentType: effectivePaymentType,
      isEdit: true,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      addOnsCatalog: experienceDetail?.add_ons,
      cardFeeInfo,
      originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, true),
      dayAdjustments: experienceDetail?.day_adjustments,
      seasonalAdjustments: experienceDetail?.seasonal_adjustments,
      seasonRanges: experienceDetail?.available_date_ranges,
      depositFraction,
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
    if (total !== sel?.amount) {
      updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromos, experienceDetail?.id])

  // Render step 1 content
  const renderStep1 = () => (
    <>
      <Card className={styles.cardContainer}>
        <Title className={styles.cardTitle} level={5}>
          {t("booking.client_detail")}
        </Title>
        <ClientDetail form={form} bookingId={bookingUuid} alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      </Card>
      <Card className={styles.cardContainer}>
        <Title className={styles.cardTitle} level={5}>
          {t("booking.experience_details")}
        </Title>
        <ExperienceDetail
          form={form}
          bookingId={bookingUuid}
          booking={booking}
          isEdit={true}
          bookAgain={bookAgain}
          cardFeeInfo={cardFeeInfo}
          appliedPromos={appliedPromos}
          isExperienceChanged={isExperienceChanged}
          bookingSelections={bookingSelections}
          updateBookingSelections={updateBookingSelections}
        />
      </Card>
    </>
  )

  // Render step 2 content
  const renderStep2 = () => (
    <>
      <div className={styles.tripDetailsCard}>
        <Title level={5} className={styles.tripDetailsTitle}>
          {t("booking.trip_details")}
        </Title>
        <TripDetailsCard booking={booking} bookingSelections={bookingSelections} experienceDetail={experienceDetail} />
      </div>

      {!showUpdatedBreakdown && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.trip_price_not_changed")}</span>
        </div>
      )}
    </>
  )

  // Render price comparison
  const renderPriceComparison = () => {
    const addOnsSelector = experienceDetail?.add_ons?.some((a) => a.enabled !== false) ? (
      <AddOnsSelector addOns={experienceDetail.add_ons} selections={bookingSelections?.addOns} onChange={handleAddOnsChange} />
    ) : null

    if (!showUpdatedBreakdown) {
      return (
        <Card className={`${styles.cardContainer} ${styles.column}`}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.price_breakdown")}
          </Title>
          {addOnsSelector}
          <PromoCodeInput
            totalAmount={bookingSelections?.baseAmount ?? 0}
            addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
            appliedPromos={appliedPromos}
            onPromoApplied={onPromoApplied}
            onPromoRemoved={onPromoRemoved}
            experienceId={experienceDetail?.id}
            clientIdentifier={clientEmail || clientPhone?.fullNumber || booking?.client?.email || booking?.client?.phone_number}
          />
          <PreviousPriceDetail form={form} booking={booking} isEdit={true} />
        </Card>
      )
    }

    return (
      <div className={styles.comparisonContainer}>
        <Card className={styles.comparisonCard}>
          <Title className={styles.comparisonTitle} level={5}>
            {t("booking.previous_price_details")}
          </Title>
          <PreviousPriceDetail form={form} booking={booking} isEdit={true} />
        </Card>
        <Card className={styles.comparisonCard}>
          <Title className={styles.comparisonTitle} level={5}>
            {t("booking.updated_price_details")}
          </Title>
          {addOnsSelector}
          <PromoCodeInput
            totalAmount={bookingSelections?.baseAmount ?? 0}
            addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
            appliedPromos={appliedPromos}
            onPromoApplied={onPromoApplied}
            onPromoRemoved={onPromoRemoved}
            experienceId={experienceDetail?.id}
            clientIdentifier={clientEmail || clientPhone?.fullNumber || booking?.client?.email || booking?.client?.phone_number}
          />
          <PriceBreakdown
            form={form}
            booking={booking}
            isEdit={true}
            isPriceDecreased={isPriceDecreased}
            hasDepositPayment={hasDepositPayment}
            non_refundable_amount={non_refundable_amount}
            appliedPromos={appliedPromos}
            bookingSelections={bookingSelections}
          />
        </Card>
      </div>
    )
  }

  // Render refund section
  const renderRefundSection = () => {
    const showRefundPayment = hasCashPayment || isLessThan24h

    return (
      <div style={{ display: isStep2 && refundDueNow > 0 ? "block" : "none" }}>
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.refund_payment")}
          </Title>
          <div className={styles.infoNotice} style={{ display: refundAlert && !hasCashPayment ? "block" : "none" }}>
            <InfoCircleOutlined className={styles.infoNoticeIcon} />
            <span className={styles.infoNoticeText}>{refundAlert}</span>
          </div>
          <div style={{ display: showRefundPayment ? "block" : "none" }}>
            <RefundPayment
              form={form}
              refundDueNow={refundDueNow}
              isStep2={isStep2}
              booking={booking}
              bookingUuid={bookingUuid}
              cash_incomes={cash_incomes}
              card_incomes={card_incomes}
            />
          </div>
        </Card>
      </div>
    )
  }

  // Render price decrease notices
  const renderPriceDecreaseNotices = () => (
    <>
      {priceDecreasedNoRefund && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.price_decreased_no_refund")}</span>
        </div>
      )}
      {depositDecreasedNoRefund && !refundDueNow && balanceAmount > 0 && (
        <div className={styles.infoNotice}>
          <InfoCircleOutlined className={styles.infoNoticeIcon} />
          <span className={styles.infoNoticeText}>{t("booking.price_decreased")}</span>
        </div>
      )}
    </>
  )

  // Render payment method section
  const renderPaymentMethod = () =>
    isStep2 &&
    isPriceChanged &&
    refundDueNow <= 0 &&
    !priceDecreasedNoRefund && (
      <div className={styles.twoColumnLayout} style={{ display: depositDecreasedNoRefund ? "none" : "block" }}>
        <Card className={`${styles.cardContainer} ${styles.column}`}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.payment_method")}
          </Title>
          <PaymentMethod
            form={form}
            bookingId={bookingUuid}
            booking={booking}
            isEdit={true}
            bookAgain={bookAgain}
            isPriceChanged={isPriceChanged}
            appliedPromos={appliedPromos}
            bookingSelections={bookingSelections}
            updateBookingSelections={updateBookingSelections}
          />
        </Card>
      </div>
    )

  // Render form actions
  const renderFormActions = () => (
    <Form.Item className={styles.formActions}>
      <Space>
        {isStep1 ? (
          <>
            <Button variant="outlined" color="primary" htmlType="button" onClick={onCancelModal}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" htmlType="button" onClick={goToStep2} disabled={!isClientDetailsFilled}>
              {t("global.next")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" color="primary" htmlType="button" onClick={goToStep1}>
              {t("global.back_to_previous_step")}
            </Button>
            <Button type="primary" htmlType="button" onClick={onSubmit}>
              {bookingUuid && !bookAgain ? t("global.update_trip") : t("global.submit")}
            </Button>
          </>
        )}
      </Space>
    </Form.Item>
  )

  return (
    <CustomForm layout="vertical" form={form} onFinish={onSubmit} onValuesChange={onValuesChange}>
      <Stepper activeStep={currentStep} t={t} />
      {/* Registered so useWatch("adjustedPrice") reliably tracks the value seeded from the booking —
          without a mounted Form.Item the watch returns undefined and the adjusted base is dropped. */}
      <Form.Item name="adjustedPrice" hidden noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="adjustmentReason" hidden noStyle>
        <input type="hidden" />
      </Form.Item>
      <div className={styles.scrollContainer}>
        {isStep1 ? (
          renderStep1()
        ) : (
          <>
            {renderStep2()}
            {renderPriceComparison()}
            {renderRefundSection()}
            {renderPriceDecreaseNotices()}
            {renderPaymentMethod()}
          </>
        )}
      </div>
      {renderFormActions()}
    </CustomForm>
  )
}

EditBookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  onValuesChange: PropTypes.func,
  appliedPromos: PropTypes.array,
  onPromoApplied: PropTypes.func,
  onPromoRemoved: PropTypes.func,
  isExperienceChanged: PropTypes.bool,
  bookingSelections: PropTypes.object,
  updateBookingSelections: PropTypes.func
}

export default EditBookingForm
